import React, { useState, useMemo } from 'react';
import { base44 } from '@/api/base44Client';
import {
  Loader2, FileText, Download, Search, Link2, Unlink, Plus, X, Receipt, Calendar,
} from 'lucide-react';
import { useToast } from '@/components/ui/use-toast';

// Per-consumer-account invoice panel.
// Shows the consumer (retail) Orders/invoices already linked to this account,
// and lets the admin allocate existing unallocated Orders to the account (or
// unlink one). "Linked" = Order.consumer_account_id === account.id.

const fmt = (n) => `£${(Number(n) || 0).toFixed(2)}`;

function orderDate(o) {
  if (o.sage_invoice_date) return new Date(o.sage_invoice_date + 'T00:00:00');
  if (o.created_date) return new Date(o.created_date);
  return null;
}
function invNo(o) {
  return o.sage_invoice_number || o.invoice_number || '—';
}

export default function ConsumerAccountInvoicePanel({ accountId, accountName, orders, onAllocated, onUnallocated }) {
  const { toast } = useToast();
  const [showAllocate, setShowAllocate] = useState(false);
  const [search, setSearch] = useState('');
  const [allocating, setAllocating] = useState(null);
  const [unallocating, setUnallocating] = useState(null);
  const [generatingId, setGeneratingId] = useState(null);

  // Open an invoice PDF for the auditor. If a rendered invoice already exists
  // (invoice_url) open that; otherwise generate one on the fly from whatever
  // data we have on the order via the shared invoice renderer.
  const openPdf = async (o) => {
    if (o.invoice_url) { window.open(o.invoice_url, '_blank'); return; }
    setGeneratingId(o.id);
    try {
      const res = await base44.functions.invoke('previewInvoice', { order: o });
      const dataUrl = res?.data?.dataUrl;
      if (!dataUrl) {
        toast({ title: 'PDF failed', description: res?.data?.error || 'Unknown error', variant: 'destructive' });
        return;
      }
      const base64 = dataUrl.split(',')[1];
      const mime = (dataUrl.match(/data:([^;]+)/) || [])[1] || 'application/pdf';
      const blob = new Blob([Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))], { type: mime });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast({ title: 'PDF failed', description: e.message, variant: 'destructive' });
    }
    setGeneratingId(null);
  };

  // Every order linked to this account — matches the count badge on the row.
  // (Includes orders without an invoice number, e.g. eBay sales, so they still
  // show up here rather than being silently filtered out.)
  const linked = useMemo(
    () => (orders || []).filter((o) => o.consumer_account_id === accountId),
    [orders, accountId]
  );

  // Unallocated orders that carry an invoice — candidates for allocation
  const unallocated = useMemo(
    () => (orders || []).filter(
      (o) => !o.consumer_account_id && (o.invoice_number || o.invoice_url || o.sage_invoice_number)
    ),
    [orders]
  );

  const filteredUnallocated = useMemo(() => {
    if (!search) return unallocated;
    const q = search.toLowerCase();
    return unallocated.filter((o) =>
      (o.invoice_number || '').toLowerCase().includes(q) ||
      (o.sage_invoice_number || '').toLowerCase().includes(q) ||
      (o.customer_name || '').toLowerCase().includes(q) ||
      (o.customer_email || '').toLowerCase().includes(q)
    );
  }, [unallocated, search]);

  const allocate = async (order) => {
    setAllocating(order.id);
    try {
      await base44.entities.Order.update(order.id, { consumer_account_id: accountId });
      if (onAllocated) onAllocated(order.id, accountId);
      toast({ title: 'Invoice allocated', description: `${invNo(order)} → ${accountName}` });
    } catch (e) {
      toast({ title: 'Allocation failed', description: e.message, variant: 'destructive' });
    }
    setAllocating(null);
  };

  const unallocate = async (order) => {
    setUnallocating(order.id);
    try {
      await base44.entities.Order.update(order.id, { consumer_account_id: null });
      if (onUnallocated) onUnallocated(order.id);
      toast({ title: 'Invoice unlinked', description: invNo(order) });
    } catch (e) {
      toast({ title: 'Unlink failed', description: e.message, variant: 'destructive' });
    }
    setUnallocating(null);
  };

  const linkedTotal = linked.reduce((s, o) => s + (Number(o.amount) || 0), 0);

  return (
    <div className="space-y-3">
      {/* Header row */}
      <div className="flex items-center gap-3 flex-wrap">
        <p className="text-primary text-[10px] font-bold tracking-widest flex items-center gap-1.5">
          <Receipt className="w-3.5 h-3.5" /> ALLOCATED INVOICES ({linked.length})
        </p>
        {linked.length > 0 && (
          <span className="text-white/40 text-[10px] font-bold tracking-widest">TOTAL {fmt(linkedTotal)}</span>
        )}
        <button
          onClick={() => setShowAllocate(true)}
          className="ml-auto inline-flex items-center gap-1.5 border border-primary/30 bg-primary/10 hover:bg-primary hover:text-white text-primary font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> ALLOCATE INVOICES
        </button>
      </div>

      {/* Linked invoices */}
      {linked.length === 0 ? (
        <p className="text-white/30 text-xs py-4 text-center border border-dashed border-white/10">
          No orders allocated to this account yet.
        </p>
      ) : (
        <div className="overflow-x-auto border border-white/10">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 bg-[#0d0d0f]">
                <th className="text-white/40 font-bold text-left py-2 px-3">INVOICE #</th>
                <th className="text-white/40 font-bold text-left py-2 px-3">SAGE INV #</th>
                <th className="text-white/40 font-bold text-left py-2 px-3">DATE</th>
                <th className="text-white/40 font-bold text-left py-2 px-3">CUSTOMER</th>
                <th className="text-white/40 font-bold text-right py-2 px-3">AMOUNT</th>
                <th className="text-white/40 font-bold text-center py-2 px-3">PDF</th>
                <th className="text-white/40 font-bold text-center py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {linked.map((o) => {
                const d = orderDate(o);
                return (
                  <tr key={o.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2 px-3">
                      <span className="text-blue-400 font-mono font-bold">{o.invoice_number || '—'}</span>
                    </td>
                    <td className="py-2 px-3">
                      {o.sage_invoice_number ? (
                        <span className="text-orange-400 font-mono font-bold">{o.sage_invoice_number}</span>
                      ) : <span className="text-white/20">—</span>}
                    </td>
                    <td className="py-2 px-3 text-white/60">{d ? d.toLocaleDateString('en-GB') : '—'}</td>
                    <td className="py-2 px-3 text-white">{o.customer_name || '—'}</td>
                    <td className="py-2 px-3 text-right text-white font-bold">{fmt(o.amount)}</td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => openPdf(o)}
                        disabled={generatingId === o.id}
                        title={o.invoice_url ? 'Open invoice PDF' : 'Generate invoice PDF from available data'}
                        className="inline-flex items-center justify-center w-7 h-7 border border-blue-400/30 bg-blue-400/10 hover:bg-blue-400 hover:text-black text-blue-400 transition-colors disabled:opacity-50"
                      >
                        {generatingId === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileText className="w-3.5 h-3.5" />}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-center">
                      <button
                        onClick={() => unallocate(o)}
                        disabled={unallocating === o.id}
                        title="Unlink this invoice from the account"
                        className="inline-flex items-center gap-1 border border-white/15 hover:border-red-400/40 text-white/40 hover:text-red-400 font-bold text-[10px] tracking-widest px-2 py-1 transition-colors disabled:opacity-50"
                      >
                        {unallocating === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Unlink className="w-3 h-3" />} UNLINK
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Allocate modal */}
      {showAllocate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={() => setShowAllocate(false)}>
          <div className="bg-[#111115] border border-white/15 w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-white/10">
              <div>
                <p className="text-primary text-[10px] font-bold tracking-widest mb-1">ALLOCATE INVOICES</p>
                <p className="text-white font-bold text-sm">{accountName}</p>
                <p className="text-white/40 text-xs mt-0.5">Select unallocated consumer invoices to link to this account</p>
              </div>
              <button onClick={() => setShowAllocate(false)} className="text-white/30 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3 overflow-y-auto">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by invoice #, Sage ref, customer name or email…"
                  className="seo-input pl-9"
                  autoFocus
                />
              </div>
              {filteredUnallocated.length === 0 ? (
                <p className="text-white/30 text-xs text-center py-8">
                  {unallocated.length === 0 ? 'No unallocated invoices available to allocate.' : 'No matches for your search.'}
                </p>
              ) : (
                <div className="space-y-2">
                  {filteredUnallocated.map((o) => {
                    const d = orderDate(o);
                    return (
                      <div key={o.id} className="border border-white/10 bg-[#0d0d0f] p-3 flex items-center gap-3">
                        <Link2 className="w-4 h-4 text-white/30 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-blue-400 font-mono font-bold text-xs">{invNo(o)}</span>
                            {o.sage_invoice_number && o.invoice_number && (
                              <span className="text-white/30 text-[9px]">· Sage {o.sage_invoice_number}</span>
                            )}
                            <span className="text-white/40 text-xs flex items-center gap-1"><Calendar className="w-3 h-3" />{d ? d.toLocaleDateString('en-GB') : '—'}</span>
                          </div>
                          <p className="text-white text-xs mt-0.5">{o.customer_name || '—'} <span className="text-white/30">· {o.customer_email || ''}</span></p>
                        </div>
                        <span className="text-white font-bold text-xs">{fmt(o.amount)}</span>
                        <button
                          onClick={() => allocate(o)}
                          disabled={allocating === o.id}
                          className="inline-flex items-center gap-1.5 border border-green-400/30 bg-green-400/10 hover:bg-green-400 hover:text-black text-green-400 font-bold text-[10px] tracking-widest px-3 py-1.5 transition-colors disabled:opacity-50"
                        >
                          {allocating === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} ASSIGN
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-white/10 flex items-center justify-between">
              <span className="text-white/30 text-[10px] font-bold tracking-widest">{unallocated.length} UNALLOCATED TOTAL</span>
              <button onClick={() => setShowAllocate(false)} className="border border-white/15 hover:border-white/30 text-white/50 hover:text-white font-bold text-[10px] tracking-widest px-4 py-2 transition-colors">CLOSE</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}